// Branded empty-state primitive. Replaces the prior "text + generic
// Polaris EmptySearch icon" pattern on the 8 data pages that surface a
// nothing-to-show moment (Interactions, Food, Reminders, Family,
// Pillbox, Videos, PillIdentify, Shopping).
//
// Each empty state has a single warm watercolor illustration centered
// above the title + body + optional CTA. The illustrations live in
// public/illustrations/empty/<slug>.png and are intentionally raster
// (rather than SVG) because they were generated as soft watercolor art
// where SVG-tracing would destroy the texture.

import type { ReactNode } from 'react';

type EmptyKind =
  | 'interactions'
  | 'food'
  | 'reminders'
  | 'family'
  | 'pillbox'
  | 'videos'
  | 'pill-identify'
  | 'shopping';

const ART_PATH: Record<EmptyKind, string> = {
  interactions: '/illustrations/empty/interactions-empty.png',
  food: '/illustrations/empty/food-empty.png',
  reminders: '/illustrations/empty/reminders-empty.png',
  family: '/illustrations/empty/family-empty.png',
  pillbox: '/illustrations/empty/pillbox-empty.png',
  videos: '/illustrations/empty/videos-empty.png',
  'pill-identify': '/illustrations/empty/pill-identify-empty.png',
  shopping: '/illustrations/empty/shopping-empty.png',
};

interface EmptyIllustrationProps {
  kind: EmptyKind;
  title: string;
  body: string;
  /** Optional CTA: rendered as the child below the body (typically a
   *  <Link className="home-hero__cta"> or <SeniorButton>). */
  cta?: ReactNode;
  /** Override illustration display width. Default 240px (renders ~360px
   *  on retina; balances "feels generous" without dominating the page). */
  artWidth?: number;
}

export function EmptyIllustration({
  kind,
  title,
  body,
  cta,
  artWidth = 240,
}: EmptyIllustrationProps): JSX.Element {
  return (
    <section className="empty-state" style={{ textAlign: 'center', padding: '32px 16px 40px' }}>
      <img
        src={ART_PATH[kind]}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        style={{
          width: artWidth,
          height: 'auto',
          maxWidth: '70%',
          margin: '0 auto 20px',
          display: 'block',
        }}
      />
      <h3 className="empty-state__title" style={{ marginBottom: 8 }}>
        {title}
      </h3>
      <p className="empty-state__body" style={{ maxWidth: 480, margin: '0 auto 20px' }}>
        {body}
      </p>
      {cta}
    </section>
  );
}
